import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { MapPin, ShieldAlert, UserCheck, UserPlus, X as XIcon, User as UserIcon, Sparkles } from "lucide-react";
import StepLayout from "./StepLayout";

const ROLE_DEFS = [
  { code: "mgmt_rep",    label: "FSMS Management Representative", short: "Mgmt Rep",   limit: 1,      desc: "Top management's link to the FSMS. Reports performance & secures resources." },
  { code: "team_leader", label: "FSMS Team Leader",               short: "Team Leader", limit: 1,      desc: "Owns the HACCP plan day-to-day. Defines CCPs and critical limits." },
  { code: "team_member", label: "FSMS Team Member",               short: "Team Member", limit: "many", desc: "One or more operators. Execute the CCP plan, raise issues, run audits." },
];

/**
 * Eligibility — mirrors the live FSMS Team page (FSMSTeam.jsx ~ line 36).
 * Owner is always eligible. Users with no site_ids are chain-wide and
 * count for every site. Otherwise we only include users that have the
 * site in their site_ids list.
 */
function eligibleForSite(users, siteId) {
  return users.filter((u) => {
    if (u.is_archived) return false;
    if (u.role === "owner") return true;
    const sids = u.site_ids || [];
    if (sids.length === 0) return true;
    return sids.includes(siteId);
  });
}

/**
 * Pre-suggest the right person for each FSMS role at a given site.
 *
 * Preference order (top → bottom). We pick the first eligible user that
 * isn't already appointed to this role. Falls through to broader roles
 * if no exact match exists.
 */
const SUGGEST_PREF = {
  mgmt_rep:    ["manager", "owner", "supervisor"],
  team_leader: ["supervisor", "manager", "owner"],
  team_member: ["staff", "supervisor", "manager"],
};

function suggestForRole(eligible, assignments, roleCode) {
  // Exclude anyone already holding ANY role at this site — one person ·
  // one site · one FSMS role (see backend rule in fsms_team.py).
  const onSite = new Set(assignments.filter((a) => a.is_active).map((a) => a.user_id));
  const pool = eligible.filter((u) => !onSite.has(u.id));
  for (const preferred of SUGGEST_PREF[roleCode] || []) {
    const hit = pool.find((u) => u.role === preferred);
    if (hit) return hit;
  }
  return pool[0] || null;   // last-resort fallback: anyone left
}

/**
 * Step 6 — Appoint the FSMS team for each site.
 *
 * Layout mirrors the live /app/fsms-team page so owners get the same
 * mental model in the wizard. Each site has its own card with a
 * vacancy badge and an Appoint button that opens a modal with the
 * full eligible-user list (owner included).
 */
export default function Step6Fsms({ stepIndex, totalSteps, onAdvance, onBack, onFinishLater }) {
  const { notify } = useDialog();
  const [sites, setSites] = useState([]);
  const [team, setTeam] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // { siteId, defaultRole?, defaultUserId? } when the modal is open
  const [modal, setModal] = useState(null);

  const load = async () => {
    try {
      const [{ data: s }, { data: u }] = await Promise.all([
        api.get("/sites"),
        api.get("/users"),
      ]);
      const siteList = s.sites || [];
      setSites(siteList);
      // IMPORTANT: keep owner + super_admin out of the archive filter only.
      // Owner IS eligible to be appointed (matches the live page behaviour).
      setTeam((u.users || []).filter((m) => !m.is_archived && m.role !== "super_admin"));
      // The /fsms-team/site/{id} endpoint returns `{site, grouped, vacancies, total}`
      // where `grouped` is { role_code: [assignment, ...] }. The wizard's
      // earlier code mistakenly read `r.data.assignments` (always undefined),
      // which caused every role to render as "Vacant" while the backend
      // refused new appointments with "already assigned" — symptoms reported
      // by the user on 2026-06-19.
      const all = await Promise.all(
        siteList.map((site) => api.get(`/fsms-team/site/${site.id}`)
          .then((r) => Object.values(r.data.grouped || {}).flat())
          .catch(() => [])),
      );
      setAssignments(all.flat());
    } catch (e) { console.debug("step6 load failed", e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const revoke = async (assignmentId) => {
    setBusy(true); setErr("");
    try {
      await api.post(`/fsms-team/${assignmentId}/revoke`, { reason: "Replaced during onboarding" });
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't revoke",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    } finally { setBusy(false); }
  };

  // "Appoint suggested" — opens the AppointModal pre-populated with the
  // recommended user, so the owner can confirm (or pick a different one
  // from the full eligible-users list) before the API call fires.
  // Previously this POSTed silently with no visual feedback, which the
  // user reported as broken ("nothing seems to happen").
  const openWithSuggested = (siteId, roleCode, userId) => {
    setModal({ siteId, defaultRole: roleCode, defaultUserId: userId });
  };

  const onAppointed = async () => {
    setModal(null);
    await load();
  };

  return (
    <StepLayout
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Appoint your FSMS committee"
      subtitle="ISO 22000 needs a named Mgmt Rep, Team Leader and at least one Team Member per site."
      description={
        <div className="space-y-3">
          <p>Every site needs at least one <strong>Management Representative</strong>, one <strong>Team Leader</strong>, and one or more <strong>Team Members</strong>. A vacancy past 7 days triggers a red banner across the app.</p>
          <p className="text-xs opacity-70">You can re-appoint or rotate roles anytime later from the FSMS Team page.</p>
        </div>
      }
      onBack={onBack}
      onNext={() => onAdvance({ fsms: true })}
      onFinishLater={onFinishLater}
      busy={busy}
      errorMsg={err}
    >
      <div className="space-y-5">
        {sites.length === 0 ? (
          <div className="vb-card p-6 text-sm opacity-70" data-testid="wiz-fsms-no-sites">
            No sites available — please complete Step 2b first.
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            {sites.map((s) => {
              const siteAssignments = assignments.filter((a) => a.site_id === s.id && a.is_active);
              const vacancies = ROLE_DEFS.reduce((sum, r) => {
                const got = siteAssignments.filter((a) => a.fsms_role === r.code).length;
                if (r.limit === "many") return sum + (got === 0 ? 1 : 0);
                return sum + Math.max(0, r.limit - got);
              }, 0);
              return (
                <SiteCard
                  key={s.id}
                  site={s}
                  assignments={siteAssignments}
                  vacancies={vacancies}
                  eligible={eligibleForSite(team, s.id)}
                  onAppoint={(roleCode) => setModal({ siteId: s.id, defaultRole: roleCode })}
                  onAppointSuggested={openWithSuggested}
                  onRevoke={revoke}
                  busy={busy}
                />
              );
            })}
          </div>
        )}
      </div>

      {modal && (
        <AppointModal
          site={sites.find((x) => x.id === modal.siteId)}
          defaultRole={modal.defaultRole}
          defaultUserId={modal.defaultUserId}
          eligible={eligibleForSite(team, modal.siteId)}
          assignments={assignments.filter((a) => a.site_id === modal.siteId && a.is_active)}
          onClose={() => setModal(null)}
          onAppointed={onAppointed}
          notify={notify}
        />
      )}
    </StepLayout>
  );
}

function SiteCard({ site, assignments, vacancies, eligible, onAppoint, onAppointSuggested, onRevoke, busy }) {
  return (
    <div className="vb-card p-5" data-testid={`wiz-fsms-card-${site.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0 flex-1">
          <div className="vb-heading text-lg m-0 flex items-center gap-1.5" style={{ color: "var(--vb-text-2)" }}>
            <MapPin size={15} className="opacity-70" /> {site.name}
          </div>
          {vacancies > 0 ? (
            <div className="vb-pill err mt-2 inline-flex" data-testid={`wiz-fsms-vac-${site.id}`}>
              <ShieldAlert size={11} /> {vacancies} vacancy(ies)
            </div>
          ) : (
            <div className="vb-pill ok mt-2 inline-flex" data-testid={`wiz-fsms-full-${site.id}`}>
              <UserCheck size={11} /> All roles filled
            </div>
          )}
        </div>
        <button type="button"
          className="vb-btn"
          onClick={() => onAppoint(null)}
          disabled={busy}
          data-testid={`wiz-fsms-appoint-btn-${site.id}`}>
          <UserPlus size={13} /> Appoint
        </button>
      </div>

      <div className="border-t pt-3 space-y-3" style={{ borderColor: "var(--vb-border)" }}>
        {ROLE_DEFS.map((r) => {
          const filled = assignments.filter((a) => a.fsms_role === r.code);
          const limitText = r.limit === "many" ? "many" : r.limit;
          const isVacant = filled.length === 0;
          // Suggestion shows when the role has room AND we have an eligible
          // person we can recommend. We hide it once the role is filled
          // (or filled-and-many: still allow more, but no auto-suggest to
          // avoid spam-appointing every staff member).
          const showSuggestion = isVacant;
          const suggested = showSuggestion ? suggestForRole(eligible, assignments, r.code) : null;
          return (
            <div key={r.code} data-testid={`wiz-fsms-role-${site.id}-${r.code}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <UserIcon size={14} className="opacity-70" />
                <span className="text-sm font-semibold" style={{ color: "var(--vb-text-2)" }}>
                  {r.label}
                </span>
                <span className="text-[11px] opacity-60">{filled.length} / {limitText}</span>
                {isVacant ? (
                  <span className="vb-pill err text-[10px]">Vacant</span>
                ) : (
                  <span className="vb-pill ok text-[10px]">Filled</span>
                )}
                {(r.limit === "many" || isVacant) && (
                  <button type="button"
                    className="ml-auto text-xs underline opacity-70 hover:opacity-100"
                    onClick={() => onAppoint(r.code)}
                    disabled={busy}
                    data-testid={`wiz-fsms-role-appoint-${site.id}-${r.code}`}>
                    + Appoint
                  </button>
                )}
              </div>
              {suggested && (
                <div className="mt-1.5 ml-5 flex items-center gap-2 flex-wrap rounded p-2"
                  style={{ background: "var(--vb-bg-2)", border: "1px dashed var(--vb-accent)" }}
                  data-testid={`wiz-fsms-suggest-${site.id}-${r.code}`}>
                  <Sparkles size={11} style={{ color: "var(--vb-accent)" }} />
                  <span className="text-xs">
                    Suggested:&nbsp;
                    <strong>{suggested.name}</strong>
                    <span className="opacity-60"> ({suggested.role}{suggested.company_role ? ` · ${suggested.company_role}` : ""})</span>
                  </span>
                  <button type="button"
                    className="vb-btn ok sm ml-auto"
                    onClick={() => onAppointSuggested(site.id, r.code, suggested.id)}
                    disabled={busy}
                    data-testid={`wiz-fsms-suggest-appoint-${site.id}-${r.code}`}>
                    Review &amp; appoint
                  </button>
                </div>
              )}
              {filled.length === 0 && !suggested ? (
                <p className="text-xs opacity-60 italic mt-1 ml-5">Not yet appointed.</p>
              ) : filled.length > 0 ? (
                <div className="mt-1.5 ml-5 space-y-1">
                  {filled.map((a) => (
                    <div key={a.id}
                      className="flex items-center gap-2 text-sm py-1"
                      data-testid={`wiz-fsms-appt-${a.id}`}>
                      <div className="flex-1 min-w-0">
                        <strong>{a.user_name}</strong>
                        <span className="opacity-60 text-xs ml-2">{a.user_email}</span>
                      </div>
                      <button type="button" className="vb-btn ghost sm"
                        onClick={() => onRevoke(a.id)}
                        disabled={busy}
                        style={{ color: "var(--vb-error)" }}
                        data-testid={`wiz-fsms-revoke-${a.id}`}>
                        <XIcon size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AppointModal({ site, defaultRole, defaultUserId, eligible, assignments, onClose, onAppointed, notify }) {
  const [roleCode, setRoleCode] = useState(defaultRole || "mgmt_rep");
  const [userId, setUserId] = useState(defaultUserId || "");
  const [busy, setBusy] = useState(false);

  // Rule (added 2026-06-19): one person · one site · one FSMS role.
  // - Hide users already appointed to any role at this site (any role,
  //   not just the current one). They must be revoked first if you want
  //   to reassign them to a different role.
  // - The same user is still eligible at other sites.
  const filteredEligible = useMemo(() => {
    const onSite = new Set(assignments.map((a) => a.user_id));
    return eligible.filter((u) => !onSite.has(u.id));
  }, [eligible, assignments]);

  // If the modal was opened from "Appoint suggested" and the suggested
  // user is filtered out (e.g. role changed), clear the selection.
  useEffect(() => {
    if (userId && !filteredEligible.find((u) => u.id === userId)) {
      setUserId("");
    }
  }, [filteredEligible, userId]);

  const suggestedUser = defaultUserId
    ? filteredEligible.find((u) => u.id === defaultUserId)
    : null;

  const submit = async () => {
    if (!userId) {
      return notify({ title: "Pick a user", message: "Select a team member to appoint.", variant: "err" });
    }
    setBusy(true);
    try {
      await api.post("/fsms-team", {
        user_id: userId,
        site_id: site.id,
        fsms_role: roleCode,
      });
      await onAppointed();
    } catch (ex) {
      await notify({
        title: "Couldn't appoint",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(60, 40, 24, 0.6)" }}
      data-testid="wiz-fsms-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="vb-card max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>
            Appoint FSMS member · {site.name}
          </h2>
          <button onClick={onClose} className="opacity-60 hover:opacity-100" data-testid="wiz-fsms-modal-close">
            <XIcon size={18} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Role *</label>
            <select className="vb-input" value={roleCode}
              onChange={(e) => { setRoleCode(e.target.value); setUserId(""); }}
              data-testid="wiz-fsms-modal-role">
              {ROLE_DEFS.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
            <p className="text-[11px] opacity-60 mt-1 m-0">
              {ROLE_DEFS.find((r) => r.code === roleCode)?.desc}
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Team member *</label>
            {suggestedUser && (
              <div className="vb-pill text-[11px] mb-2"
                style={{ background: "var(--vb-bg-2)", border: "1px dashed var(--vb-accent)" }}
                data-testid="wiz-fsms-modal-suggested-banner">
                <Sparkles size={11} style={{ color: "var(--vb-accent)" }} />
                Suggested: <strong className="ml-1">{suggestedUser.name}</strong>
                <span className="opacity-60 ml-1">
                  ({suggestedUser.role}{suggestedUser.company_role ? ` · ${suggestedUser.company_role}` : ""})
                </span>
                <span className="opacity-60 ml-1">— pre-selected. Pick someone else below if needed.</span>
              </div>
            )}
            {filteredEligible.length === 0 ? (
              <div className="vb-pill warn">
                No more eligible team members for this role at this site. Invite someone in Step 3, or pick another role.
              </div>
            ) : (
              <select className="vb-input" value={userId}
                onChange={(e) => setUserId(e.target.value)}
                data-testid="wiz-fsms-modal-user">
                <option value="">— Pick a team member —</option>
                {filteredEligible.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.role}{u.company_role ? ` · ${u.company_role}` : ""})
                    {defaultUserId === u.id ? "  ⭐ suggested" : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" className="vb-btn ghost" onClick={onClose} disabled={busy}
            data-testid="wiz-fsms-modal-cancel">
            Cancel
          </button>
          <button type="button" className="vb-btn" onClick={submit}
            disabled={busy || !userId || filteredEligible.length === 0}
            data-testid="wiz-fsms-modal-save">
            {busy ? "Appointing…" : "Appoint"}
          </button>
        </div>
      </div>
    </div>
  );
}
